import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Icon,
  Button,
  Card,
  CardHeader,
  CardBody,
  Input,
  Badge,
  showToast,
} from '@repo/ui';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  useContactsReplicationMetadata,
  useUpdateContactsReplicationMetadata,
  useSyncContactFromClio,
} from '../queries';
import {
  dataReplicationApi,
  type ContactSearchType,
  type ContactSearchResult,
} from '../api';
import { api } from '@xtnd/web/shared/api';
import { canResetReplication } from '../utils/accessControl';
import { pickReplicationTimestamp } from '../utils/replication';

type SearchTypeOption = {
  value: ContactSearchType;
  label: string;
};

const SEARCH_TYPE_OPTIONS: SearchTypeOption[] = [
  { value: 'contactId', label: 'Contact ID' },
  { value: 'name', label: 'Name' },
  { value: 'email', label: 'Email' },
];

export default function ClioContactsReplicationPage() {
  const navigate = useNavigate();

  // State
  const [searchValue, setSearchValue] = useState('');
  const [searchType, setSearchType] = useState<ContactSearchType>('contactId');
  const [isSearching, setIsSearching] = useState(false);
  const [dynamoDBResult, setDynamoDBResult] = useState<ContactSearchResult | null>(null);
  const [clioResult, setClioResult] = useState<ContactSearchResult | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  // Matter related contacts resync state
  const [matterIdInput, setMatterIdInput] = useState('');
  const [isMatterSyncing, setIsMatterSyncing] = useState(false);
  const [matterSyncResult, setMatterSyncResult] = useState<{ syncedCount: number; contacts: any[] } | null>(null);

  // Queries and mutations
  const { data: metadataResponse, isLoading: isLoadingMetadata, refetch } = useContactsReplicationMetadata();
  const updateMetadataMutation = useUpdateContactsReplicationMetadata();
  const syncContactMutation = useSyncContactFromClio();

  const metadata = metadataResponse?.metadata;
  const contactsCount = metadataResponse?.contactsCount ?? 0;
  const canReset = canResetReplication();

  // Format date helper
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  // Handle reset metadata
  const handleResetMetadata = async () => {
    try {
      await updateMetadataMutation.mutateAsync(null);
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // Handle matter related contacts resync
  const handleMatterResync = async () => {
    const matterId = matterIdInput.trim();
    if (!matterId) {
      showToast.error('Please enter a Matter ID');
      return;
    }
    setIsMatterSyncing(true);
    setMatterSyncResult(null);
    try {
      const result: any = await api.post(`/clio/matters/${matterId}/resync-related-contacts`);
      setMatterSyncResult({ syncedCount: result.syncedCount, contacts: result.contacts || [] });
      showToast.success(result.message || `Synced ${result.syncedCount} related contacts`);
    } catch (error: any) {
      showToast.error(error?.response?.data?.message || error?.message || 'Failed to resync matter contacts');
    } finally {
      setIsMatterSyncing(false);
    }
  };

  // Handle search
  const handleSearch = async () => {
    if (!searchValue.trim()) {
      showToast.error('Please enter a search value');
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    setDynamoDBResult(null);
    setClioResult(null);

    try {
      // First search in DynamoDB
      const dbResult = await dataReplicationApi.searchContactInDynamoDB(searchValue.trim(), searchType);
      setDynamoDBResult(dbResult);

      // If not found in DynamoDB, search in Clio
      if (!dbResult.found) {
        const clResult = await dataReplicationApi.searchContactInClio(searchValue.trim(), searchType);
        setClioResult(clResult);
      }
    } catch (error: any) {
      showToast.error(`Search failed: ${error.message}`);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle sync/resync contact
  const handleSyncContact = async (contactId: string) => {
    try {
      await syncContactMutation.mutateAsync(contactId);
      // Re-run the search to show updated data
      await handleSearch();
    } catch (error) {
      // Error handled in mutation hook
    }
  };

  // Render contact details
  const renderContactDetails = (contact: any, source: string) => {
    return (
      <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h4 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
            Contact Details
          </h4>
          <Badge variant={source === 'dynamodb' ? 'success' : 'warning'}>
            {source === 'dynamodb' ? 'From XTND' : 'From Clio'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Contact ID:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {contact.contactId || contact.id || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Name:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {contact.name || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Type:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {contact.type || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Email:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {contact.primaryEmailAddress || contact.primary_email_address || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Phone:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {contact.primaryPhoneNumber || contact.primary_phone_number || 'N/A'}
            </p>
          </div>
          <div>
            <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Status:</span>
            <p className="font-medium text-xtnd-dark-800 dark:text-white">
              {contact.isClient ? 'Client' : contact.isCoCounsel ? 'Co-Counsel' : 'Contact'}
            </p>
          </div>
          {source === 'dynamodb' && contact.replicatedAt && (
            <div className="col-span-2 md:col-span-3">
              <span className="text-xtnd-dark-400 dark:text-xtnd-dark-300">Last Replicated:</span>
              <p className="font-medium text-xtnd-dark-800 dark:text-white">
                {formatDate(contact.replicatedAt)}
              </p>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <PageLayout
      title="Clio Contacts Replication"
      description="Manage and monitor Clio contacts data replication"
      icon="HiDatabase"
      isLoading={isLoadingMetadata}
      breadcrumbs={[
        { label: 'Settings', href: '/settings' },
        { label: 'Data Replication' },
        { label: 'Clio Contacts' },
      ]}
    >
      <div className="space-y-6">
        {/* Back button */}
        <Button
          variant="outline"
          size="sm"
          leftIcon={<Icon name="HiArrowLeft" className="h-4 w-4" />}
          onClick={() => navigate('/settings')}
        >
          Back to Settings
        </Button>

        {/* Replication Status Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Icon name="HiInformationCircle" className="h-6 w-6 text-xtnd-blue" />
                <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                  Replication Status
                </h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                onClick={() => refetch()}
              >
                Refresh
              </Button>
            </div>
          </CardHeader>
          <CardBody>
            <div className={`grid grid-cols-1 ${canReset ? 'md:grid-cols-3' : 'md:grid-cols-2'} gap-6`}>
              {/* Contacts Count */}
              <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="HiCollection" className="h-8 w-8 text-xtnd-blue" />
                  <div>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Total Contacts
                    </p>
                    <p className="text-2xl font-bold text-xtnd-dark-800 dark:text-white">
                      {contactsCount.toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Last Replication */}
              <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                <div className="flex items-center space-x-3">
                  <Icon name="HiClock" className="h-8 w-8 text-green-500" />
                  <div>
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Last Replication
                    </p>
                    <p className="text-sm font-medium text-xtnd-dark-800 dark:text-white">
                      {formatDate(pickReplicationTimestamp(metadata))}
                    </p>
                  </div>
                </div>
              </div>

              {/* Reset Action - Only visible to authorized users */}
              {canReset && (
                <div className="bg-xtnd-dark-50 dark:bg-xtnd-dark-700 rounded-lg p-4">
                  <div className="flex flex-col space-y-2">
                    <p className="text-sm text-xtnd-dark-400 dark:text-xtnd-dark-300">
                      Reset Replication
                    </p>
                    <p className="text-xs text-xtnd-dark-300 dark:text-xtnd-dark-400 mb-2">
                      Clear the last replication date to force a full sync on the next scheduled run
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleResetMetadata}
                      loading={updateMetadataMutation.isPending}
                      leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                    >
                      Reset to Force Full Sync
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </CardBody>
        </Card>

        {/* Contact Search Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Icon name="HiSearch" className="h-6 w-6 text-xtnd-blue" />
              <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                Search & Sync Contact
              </h3>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              {/* Search Form */}
              <div className="flex flex-col sm:flex-row gap-4">
                {/* Search Type Dropdown */}
                <div className="sm:w-48">
                  <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                    Search By
                  </label>
                  <select
                    value={searchType}
                    onChange={(e) => setSearchType(e.target.value as ContactSearchType)}
                    className="w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md bg-white dark:bg-xtnd-dark-700 text-xtnd-dark-800 dark:text-white focus:ring-xtnd-blue focus:border-xtnd-blue"
                  >
                    {SEARCH_TYPE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Search Input */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                    Search Value
                  </label>
                  <Input
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder={`Enter ${SEARCH_TYPE_OPTIONS.find(o => o.value === searchType)?.label}...`}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  />
                </div>

                {/* Search Button */}
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    onClick={handleSearch}
                    loading={isSearching}
                    leftIcon={<Icon name="HiSearch" className="h-4 w-4" />}
                  >
                    Search
                  </Button>
                </div>
              </div>

              {/* Search Results */}
              {hasSearched && (
                <div className="mt-6 space-y-4">
                  <h4 className="text-md font-medium text-xtnd-dark-800 dark:text-white">
                    Search Results
                  </h4>

                  {/* Loading state */}
                  {isSearching && (
                    <div className="text-center py-8">
                      <Icon name="HiRefresh" className="h-8 w-8 text-xtnd-blue animate-spin mx-auto" />
                      <p className="mt-2 text-xtnd-dark-400 dark:text-xtnd-dark-300">Searching...</p>
                    </div>
                  )}

                  {/* DynamoDB Result */}
                  {!isSearching && dynamoDBResult && (
                    <div className="space-y-4">
                      {dynamoDBResult.found && dynamoDBResult.contact ? (
                        <>
                          {renderContactDetails(dynamoDBResult.contact, 'dynamodb')}
                          <div className="flex justify-end">
                            <Button
                              variant="secondary"
                              onClick={() => handleSyncContact(
                                dynamoDBResult.contact?.contactId ||
                                dynamoDBResult.contact?.id?.toString() ||
                                searchValue
                              )}
                              loading={syncContactMutation.isPending}
                              leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                            >
                              Resync from Clio
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <Icon name="HiExclamation" className="h-5 w-5 text-yellow-500" />
                            <p className="text-sm text-yellow-700 dark:text-yellow-300">
                              Contact not found in local cache (DynamoDB)
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Clio Result (only shown if not in DynamoDB) */}
                  {!isSearching && !dynamoDBResult?.found && clioResult && (
                    <div className="space-y-4">
                      {clioResult.found && clioResult.contact ? (
                        <>
                          {renderContactDetails(clioResult.contact, 'clio')}
                          <div className="flex justify-end">
                            <Button
                              variant="primary"
                              onClick={() => handleSyncContact(
                                clioResult.contact?.id?.toString() || searchValue
                              )}
                              loading={syncContactMutation.isPending}
                              leftIcon={<Icon name="HiDownload" className="h-4 w-4" />}
                            >
                              Replicate to Local Cache
                            </Button>
                          </div>
                        </>
                      ) : (
                        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                          <div className="flex items-center space-x-2">
                            <Icon name="HiXCircle" className="h-5 w-5 text-red-500" />
                            <p className="text-sm text-red-700 dark:text-red-300">
                              Contact not found in Clio
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No results anywhere */}
                  {!isSearching && !dynamoDBResult?.found && !clioResult?.found && clioResult !== null && (
                    <div className="text-center py-8">
                      <Icon name="HiSearchCircle" className="h-12 w-12 text-xtnd-dark-300 mx-auto" />
                      <p className="mt-2 text-xtnd-dark-400 dark:text-xtnd-dark-300">
                        No contact found with the provided search criteria
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
        {/* Matter Related Contacts Sync Card */}
        <Card>
          <CardHeader>
            <div className="flex items-center space-x-3">
              <Icon name="HiUserGroup" className="h-6 w-6 text-xtnd-blue" />
              <h3 className="text-lg font-medium text-xtnd-dark-800 dark:text-white">
                Matter Related Contacts Sync
              </h3>
            </div>
          </CardHeader>
          <CardBody>
            <div className="space-y-4">
              <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Fetch related contacts for a specific matter from Clio and update the local contacts cache.
                This pulls contacts linked to the matter via relationships.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200 mb-1">
                    Matter ID
                  </label>
                  <Input
                    value={matterIdInput}
                    onChange={(e) => setMatterIdInput(e.target.value)}
                    placeholder="Enter Clio Matter ID..."
                    onKeyDown={(e) => e.key === 'Enter' && handleMatterResync()}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    variant="primary"
                    onClick={handleMatterResync}
                    loading={isMatterSyncing}
                    leftIcon={<Icon name="HiRefresh" className="h-4 w-4" />}
                  >
                    Sync Related Contacts
                  </Button>
                </div>
              </div>

              {matterSyncResult && (
                <div className="mt-4">
                  <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-3">
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Successfully synced {matterSyncResult.syncedCount} related contact{matterSyncResult.syncedCount === 1 ? '' : 's'}
                    </p>
                  </div>
                  {matterSyncResult.contacts.length > 0 && (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-xtnd-dark-200 dark:border-xtnd-dark-600">
                            <th className="text-left py-2 px-3 text-xtnd-dark-500">ID</th>
                            <th className="text-left py-2 px-3 text-xtnd-dark-500">Name</th>
                            <th className="text-left py-2 px-3 text-xtnd-dark-500">Email</th>
                            <th className="text-left py-2 px-3 text-xtnd-dark-500">Bill Recipient</th>
                            <th className="text-left py-2 px-3 text-xtnd-dark-500">Client</th>
                          </tr>
                        </thead>
                        <tbody>
                          {matterSyncResult.contacts.map((c: any) => (
                            <tr key={c.id} className="border-b border-xtnd-dark-100 dark:border-xtnd-dark-700">
                              <td className="py-2 px-3 text-xtnd-dark-800 dark:text-white">{c.id}</td>
                              <td className="py-2 px-3 text-xtnd-dark-800 dark:text-white">{c.name || 'N/A'}</td>
                              <td className="py-2 px-3 text-xtnd-dark-800 dark:text-white">{c.primary_email_address || 'N/A'}</td>
                              <td className="py-2 px-3">
                                <Badge variant={c.is_bill_recipient ? 'success' : 'default'}>
                                  {c.is_bill_recipient ? 'Yes' : 'No'}
                                </Badge>
                              </td>
                              <td className="py-2 px-3">
                                <Badge variant={c.is_client ? 'info' : 'default'}>
                                  {c.is_client ? 'Yes' : 'No'}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardBody>
        </Card>
      </div>
    </PageLayout>
  );
}
